/* modules */
import { Component, OnDestroy, OnInit, ViewEncapsulation, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { slider } from '@app/core/animations/animations';
import { tap } from 'rxjs/operators';

/* services */
/* constants */
import { environment } from '../environments/environment';
import { DialogService, GameService, InternationalizationService, PlaySoundsService, SessionService, UmbracoService, UtilitiesService } from './core/services';
import { ConfettiComponent } from './shared/confetti/confetti.component';
import { DialogConfig, GameErrorComponent } from './shared/game-error/game-error.component';
import { LoaderComponent } from './shared/loader/loader.component';
import { LogoComponent } from './shared/logo/logo.component';
import { LanguageViewModel } from './shared/rest-api';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'jeopardy-app',
  templateUrl: 'app.component.html',
  animations: [
    slider,
  ],
  imports: [RouterOutlet, LoaderComponent, LogoComponent, ConfettiComponent]
})
export class AppComponent implements OnInit, OnDestroy {

  private zoomInDuration = signal(0);
  private zoomOutDuration = signal(0);
  protected isMainPage = signal(false);

  private utilitiesService = inject(UtilitiesService);
  public sessionService = inject(SessionService);
  private gameService = inject(GameService);
  private router = inject(Router);
  private umbracoService = inject(UmbracoService);
  public i18n = inject(InternationalizationService);
  private dialogService = inject(DialogService);
  private soundsService = inject(PlaySoundsService);

  private readonly updateAppViewportHeight = () => {
    if (typeof window === 'undefined') {
      return;
    }

    window.requestAnimationFrame(() => {
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      document.documentElement.style.setProperty('--app-height', `${Math.round(viewportHeight)}px`);
    });
  };

  private setMainFontForLanguage(language?: LanguageViewModel | null): void {
    const isoCode = language?.isoCode2Letters;
    const fontVariable = isoCode && !['en', 'fr'].includes(isoCode)
      ? 'var(--special-font)'
      : 'var(--default-font)';

    document.documentElement.style.setProperty('--main-font', fontVariable);
  }

  ngOnInit() {
    this.updateAppViewportHeight();
    window.addEventListener('resize', this.updateAppViewportHeight, { passive: true });
    window.addEventListener('orientationchange', this.updateAppViewportHeight, { passive: true });
    window.visualViewport?.addEventListener('resize', this.updateAppViewportHeight, { passive: true });

    this.sessionService.checkCookiesLifeTime();

    this.zoomInDuration.set(200);
    this.zoomOutDuration.set(200);
    this.gameService.setDeviceDetails(this.utilitiesService);

    let location = this.sessionService.getLocation();

    this.setMainFontForLanguage(location?.language);

    this.umbracoService.translations$.subscribe((translations) => {
      if (translations) {
        this.sessionService.setTranslations(translations);
      }
    });
    const session = this.sessionService.getSession();
    this.soundsService.soundsOff = session?.soundsOff;
    this.soundsService.musicOff = session?.musicOff;

    if (location) {
      const sessionLanguage = this.sessionService.getLocation()?.language;
      this.umbracoService.setLanguage(sessionLanguage.isoCode4Letters);
    } else {
      const languages$ = this.umbracoService.getLanguages();

      languages$.pipe(
        tap((languages: LanguageViewModel[]) => {
          const defaultLanguage = languages.find(lang => lang.isoCode4Letters === environment.defaultLanguage);
          location = {
            ...location,
            language: defaultLanguage
          };
          this.sessionService.setLocation(location);

          this.setMainFontForLanguage(defaultLanguage);
          this.umbracoService.setLanguage(defaultLanguage.isoCode4Letters);
        })
      ).subscribe();
    }

    this.gameService.userIdle.subscribe(val => {
      if (val) {
        console.log('user idle');

        const config: DialogConfig = {
          header: this.i18n.str('challengeu.dialog_warning_title', 'Warning')(),
          message: this.i18n.str('challengeu.dialog_still_active', "Are yous still active?")(),
          acceptButtonLabel: this.i18n.str('challengeu.dialog_continue', 'CONTINUE')(),
          cancelButtonLabel: this.i18n.str('challengeu.dialog_leave', 'LEAVE')(),
        }

        const ref = this.dialogService.open(GameErrorComponent, config);

        ref.afterClosed$.subscribe(res => {
          if (!res.data) {
            this.sessionService.updateUserGameId(null);
            this.gameService.finishGame();
            this.router.navigate(['/main-menu']);
          }
        });
      }
      else {
        // TODO: close opened dialog
        // this.gameService.userIdle.next(false);
      }
    });

    this.router.events.subscribe(ev => {
      if (ev instanceof NavigationEnd) {
        this.isMainPage.update(() => ev.url === '/');
      }
    });

    this.gameService.userDisconnected.subscribe(val => {
      if (val) {
        console.log('user disconnected');

        const config: DialogConfig = {
          header: this.i18n.str('challengeu.dialog_warning_title', 'Warning')(),
          message: this.i18n.str('challengeu.dialog_user_disconnected', "You have been disconnected from the game due to inactivity.")(),
          showAcceptButton: false,
          cancelButtonLabel: this.i18n.str('challengeu.dialog_close', 'CLOSE')(),
        }

        const ref = this.dialogService.open(GameErrorComponent, config);

        ref.afterClosed$.subscribe(() => {
          this.sessionService.updateUserGameId(null);
          this.gameService.finishGame();
          this.router.navigate(['/main-menu']);
        });
      }
    });

    // get game details
    const gameId = +this.gameService.getGameId();
    if (gameId) {
      this.gameService.getGameDetails(gameId).subscribe(game => {
        this.gameService.game.next(game);
      });
    }
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.updateAppViewportHeight);
    window.removeEventListener('orientationchange', this.updateAppViewportHeight);
    window.visualViewport?.removeEventListener('resize', this.updateAppViewportHeight);
  }

  prepareRoute(outlet: RouterOutlet) {
    return outlet?.activatedRouteData?.['animation']?.pageAlias ?? null;
  }
}
